/** @format */

import { ElMessage as $message, ElLoading } from 'element-plus';
import { saveAs } from 'file-saver';
import { request } from './request';
import { getErrorMsg } from './error-msg';

/**
 * 下载选项接口
 */
export interface DownloadOptions {
	/** 请求URL */
	url: string;
	/** 请求数据 */
	data?: any;
	/** 文件名 */
	filename?: string;
	/** 请求头 */
	headers?: Record<string, any>;
	/** 是否从响应头获取文件名 */
	isGetFileName?: boolean;
	/** 请求方法 */
	method?: 'post' | 'get';
	/** 请求超时时间 */
	timeout?: number;
	/** Content-Type */
	contentType?: string;
	[key: string]: any;
}

/**
 * 通用下载方法 - 支持多种调用方式
 *
 * @example
 * // 传统位置参数调用方式（向后兼容）
 * download('/api/export', { id: 1 }, 'file.xlsx', { 'Content-Type': 'application/json' }, false);
 *
 * @example
 * // 对象参数调用方式（推荐）
 * download({
 *   url: '/api/export',
 *   data: { id: 1 },
 *   filename: 'file.xlsx',
 *   headers: { 'Content-Type': 'application/json' },
 *   method: 'post',
 *   isGetFileName: false
 * });
 *
 * @param options - 下载选项对象
 */
export function download(options: DownloadOptions): Promise<void>;

/**
 * 通用下载方法 - 传统位置参数调用方式
 *
 * @param url - 请求URL
 * @param data - 请求数据
 * @param filename - 文件名
 * @param headers - 请求头
 * @param isGetFileName - 是否从响应头获取文件名
 */
export function download(
	url: string,
	data?: any,
	filename?: string,
	headers?: Record<string, any>,
	isGetFileName?: boolean,
): Promise<void>;

export function download(
	urlOrOptions: string | DownloadOptions,
	data?: any,
	filename?: string,
	headers: Record<string, any> = {},
	isGetFileName = false,
): Promise<void> {
	// 参数归一化处理
	let options: DownloadOptions;

	if (typeof urlOrOptions === 'string') {
		// 传统位置参数调用方式
		options = {
			url: urlOrOptions,
			data,
			filename,
			headers,
			isGetFileName,
			method: 'post',
			timeout: 0,
			contentType: 'application/x-www-form-urlencoded',
		};
	} else {
		// 对象参数调用方式
		options = {
			method: 'post',
			timeout: 0,
			contentType: 'application/x-www-form-urlencoded',
			headers: {},
			isGetFileName: false,
			...urlOrOptions,
		};
	}
	const downloadLoadingInstance = ElLoading.service({ text: '正在下载数据，请稍候', background: 'rgba(0, 0, 0, 0.7)' });

	return request<any>(
		{
			url: options.url,
			method: options.method || 'post',
			data: options.data,
			timeout: options.timeout || 0,
			headers: {
				'Content-Type': options.contentType || 'application/x-www-form-urlencoded',
				...options.headers,
			},
			responseType: 'blob',
		},
		{
			isGetDataDirectly: false,
		},
	)
		.then(async (data) => {
			const isBlob = Object.prototype.toString.call(data) === '[object Blob]';
			if (isBlob) {
				let blob;
				if (data.type) {
					blob = new Blob([data as string], { type: data.type });
				} else {
					blob = new Blob([data as string]);
				}

				saveAs(
					blob,
					options.isGetFileName && data.contentDisposition
						? decodeURI(data.contentDisposition.split('=')[1])
						: options.filename,
				);
			} else {
				const fileReader = new FileReader();
				fileReader.onloadend = () => {
					const jsonData = JSON.parse(fileReader.result as any); // 说明是普通对象数据
					$message.error(getErrorMsg(jsonData as any));
				};
				fileReader.readAsText(data);
			}
			downloadLoadingInstance.close();
		})
		.catch((r) => {
			console.error(r);
			$message.error('下载文件出现错误，请联系管理员！');
			downloadLoadingInstance.close();
		});
}
